import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { BookingTransaction, GetBookingResponseBody } from 'app/data/types/booking';
import { useApi } from 'app/shared/contexts/api';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { BOOKING_QUERY_KEY } from 'app/data/queries/use-booking-query';
import { ACTIVITY_FEED_QUERY_KEY } from 'app/data/queries/use-activity-feed-query';

/** At or above this, the server failed AFTER it had already done the work. */
const SERVER_ERROR_STATUS = 500;

/** Wire value for a cancellation (resource-booking `PATCH_STATUSES.CANCELLED`). */
const CANCELLED_STATUS = 'cancelled';

interface CancelBody {
  status: string;
}

/** What the tile knows: the uuid always, the numeric id once the projection carries it. */
export interface CancelResourceBookingInput {
  transactionUuid: string;
  /** From `routingDetails.transactionId`. Absent on documents projected before helix carried it. */
  transactionId?: number;
}

/**
 * Cancel a resource booking from the feed tile.
 *
 * The two endpoints disagree about identity: the feed keys everything on `transactionUuid`, but
 * `PATCH /api/transactions/{id}` wants the transaction's NUMERIC id. The projection now carries it,
 * so the common path is a single call — the uuid-to-id lookup remains only for documents written
 * before that, which would otherwise be uncancellable.
 */
export const useCancelResourceBookingMutation = (): UseMutationResult<void, Error, CancelResourceBookingInput> => {
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();
  const queryClient = useQueryClient();

  /** Legacy path only — a document projected before helix published the numeric id. */
  const resolveTransactionId = async (transactionUuid: string): Promise<number> => {
    const headers = { ...(await getAuthHeaders()), ...getDefaultHeaders(buildingUuid) };
    const payload = await apiRequest<GetBookingResponseBody, void, void>({
      url: `${apiUrl}/api/tenant/v1/users/current/transactions/${encodeURIComponent(transactionUuid)}`,
      method: HttpMethod.GET,
      headers,
    });

    const booking: BookingTransaction | null = !payload.isEmpty() ? payload.data.data ?? null : null;
    if (!booking?.id) {
      // Better to fail loudly than to PATCH `/api/transactions/undefined`, which would read as a
      // different booking rather than as an error.
      throw new Error(`No numeric transaction id for booking ${transactionUuid}`);
    }

    return booking.id;
  };

  return useMutation<void, Error, CancelResourceBookingInput>({
    mutationFn: async ({ transactionUuid, transactionId }: CancelResourceBookingInput) => {
      const headers = { ...(await getAuthHeaders()), ...getDefaultHeaders(buildingUuid) };
      const numericId = transactionId ?? (await resolveTransactionId(transactionUuid));

      try {
        await apiRequest<void, CancelBody, void>({
          url: `${apiUrl}/api/transactions/${numericId}`,
          method: HttpMethod.PATCH,
          headers,
          body: { status: CANCELLED_STATUS },
        });
      } catch (error) {
        // helix commits the cancellation and only THEN publishes the updated event, rethrowing if
        // that publish fails — so a 5xx here still leaves the booking cancelled, and surfacing it
        // told the reader their cancellation had failed when it had not. Only 5xx is swallowed: a
        // 400 (outside the cancellation window) or a 401/404 is a real refusal the modal must show.
        // Read off the shape rather than `instanceof ApiError`: the error crosses a package
        // boundary, and a duck-typed status cannot be defeated by a duplicate class identity.
        const { statusCode } = (error ?? {}) as { statusCode?: number };
        if (typeof statusCode !== 'number' || statusCode < SERVER_ERROR_STATUS) {
          throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BOOKING_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACTIVITY_FEED_QUERY_KEY] });
    },
  });
};
